<template>
    <div :class="{'question-list--compact': compact}">
        <div v-if="compact" class="compact-question-rail" aria-label="Questions 축소 탐색">
            <div
                v-for="row in compactRail"
                :key="row.key"
                class="compact-question-row"
                :class="{active: row.active}"
                :style="{marginLeft: `${row.depth * 4}px`}"
            >
                <button
                    v-if="row.type === 'question'"
                    type="button"
                    class="compact-question-target"
                    :title="row.label"
                    :aria-label="row.label"
                    :aria-current="row.active ? 'page' : null"
                    @click="pageGo(row.source)"
                >
                    <span class="compact-question-bar"/>
                </button>
                <span v-else class="compact-question-static" :title="row.label" :aria-label="row.label">
                    <span class="compact-question-bar"/>
                </span>
            </div>
        </div>

        <template v-else>
            <b-list-group
                v-for="(lv1, idx) in new_question_list"
                :key="lv1.id || idx"
                class="mb-1"
            >
                <button type="button"
                        class="py-1 fw-500 category1s"
                        :aria-expanded="String(!!visible[idx])"
                        :aria-controls="`cat-${idx}`"
                        @click="toggleList(idx)"
                >
                    <fa :icon="visible[idx] ? 'caret-down' : 'caret-right'" class="mr-1"/>
                    {{ localizedName(lv1) }}
                </button>
                <b-collapse :id="`cat-${idx}`" v-model="visible[idx]" :class="admin_mode === 'project' ? 'in_project' : ''">
                    <b-list-group-item
                        v-for="lv3 in lv1.category3"
                        :key="lv3.question_code"
                        button
                        :aria-current="isQuestionActive(lv3.question_code) ? 'page' : null"
                        :class="[isQuestionActive(lv3.question_code) ? 'active' : '', 'category3s']"
                        @click="pageGo(lv3)"
                    >
                        {{ localizedName(lv3) }}
                    </b-list-group-item>
                </b-collapse>
            </b-list-group>
        </template>
    </div>
</template>

<script>
import {mapState} from 'vuex';

const {
    localizedQuestionName,
    flattenQuestionRail,
} = require('./audit-question-navigation-presentation.cjs');

export default {
    name: 'AuditQuestionList',
    props: {
        compact: {type: Boolean, default: false},
        // true: URL(question_code)가 단일 출처이고 문항 로딩은 부모가 route 감시로 한다 (CompanyAnswer_v2).
        // false: 예전처럼 여기서 store 의 question_composition 을 읽는다 (원청사 결과 화면, 구 CompanyAnswer)
        routeDriven: {type: Boolean, default: false},
    },
    data() {
        return {
            is_active: '',
            visible: [true, true, true, true, true, true],
        };
    },
    computed: {
        ...mapState(['template_tree', 'admin_mode']),
        new_question_list() {
            return (this.template_tree?.template_contents || []).map(lv1 => ({
                id: lv1.id,
                question_name: lv1.question_name,
                question_name_e: lv1.question_name_e,
                question_name_c: lv1.question_name_c,
                category3: (lv1.children || []).flatMap(lv2 => lv2.children || []),
            }));
        },
        compactRail() {
            return flattenQuestionRail(
                this.template_tree?.template_contents,
                this.$route.query.question_code,
                this.$i18n.locale,
            );
        },
    },
    watch: {
        new_question_list: {
            immediate: true,
            handler() {
                this.visible = this.new_question_list.map(() => true);
            },
        },
    },
    mounted() {
        this.$EventBus.$on('Answers-pipeline_overlay_question_clicked', this.pageGo);
    },
    beforeDestroy() {
        this.$EventBus.$off('Answers-pipeline_overlay_question_clicked', this.pageGo);
    },
    methods: {
        localizedName(node) {
            return localizedQuestionName(node, this.$i18n.locale);
        },
        // 활성 표시는 URL 이 단일 출처다 (문항 로딩은 부모가 $route.query.question_code 변화에 반응해 수행한다)
        isQuestionActive(questionCode) {
            const current = this.routeDriven
                ? this.$route.query.question_code
                : (this.$route.query.question_code || this.is_active);
            return String(questionCode || '') === String(current || '');
        },
        async pageGo(category3) {
            this.$emit('taxonomy', 'close');
            if (!this.routeDriven) {
                this.is_active = category3.question_code;
                this.$store.commit('update_is_question_subs_loaded', false);
                // 더 나중에 누른 문항이 있으면(false) 그 문항이 이동한다 — 여기서 옛 문항으로 URL 을 바꾸지 않는다
                const applied = await this.$store.dispatch('readQuestionComposition', category3.question_code);
                if (applied === false) return;
            }
            if (String(category3.question_code) !== String(this.$route.query.question_code || '')) {
                this.$router.push({
                    name: this.$route.name,
                    query: {...this.$route.query, question_code: category3.question_code},
                }).catch(() => {}); // 중복 이동/가드 중단은 무시
            } else {
                // 같은 문항을 다시 누르면 새로고침
                this.$emit('reset-sub-evaluation');
            }
        },
        toggleList(idx) {
            this.visible.splice(idx, 1, this.visible[idx] !== true);
        },
        goTaxonomyCheck() {
            this.is_active = 'taxonomy';
            this.$emit('taxonomy', 'open');
        },
    },
};
</script>

<style scoped>
.category1s {
    display: block;
    width: 100%;
    padding-right: 0;
    padding-left: 0;
    border: 0;
    border-bottom: 1px solid var(--gray-300);
    background: transparent;
    color: inherit;
    text-align: left;
}

.category3s {
    border: 0;
    border-radius: 0;
    letter-spacing: -0.2px;
    cursor: pointer;
    background-color: var(--gray-100);
    font-size: 0.9rem;
    color: var(--gray-600);
    padding: 0.25rem 0.5rem;
}

.category3s:hover,
.category3s:focus {
    background-color: var(--gray-300);
    color: var(--gray-600);
}

.category3s.active,
[id^='cat'] > .list-group-item.active {
    background-color: var(--blue);
    color: var(--gray-100);
    border: 0;
}

.category3s.active:hover,
.category3s.active:focus {
    background-color: var(--blue5);
    color: var(--gray-100);
}

.in_project > .category3s.active {
    background-color: var(--teal);
    color: var(--gray-100);
}

.in_project > .category3s.active:hover,
.in_project > .category3s.active:focus {
    background-color: var(--teal5);
}

.compact-question-rail {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    padding: 1px 0;
}

.compact-question-row,
.compact-question-target,
.compact-question-static {
    width: 16px;
    height: 3px;
    display: flex;
    align-items: center;
    justify-content: flex-start;
}

.compact-question-target {
    padding: 0;
    border: 0;
    background: transparent;
    cursor: pointer;
}

.compact-question-static {
    cursor: default;
}

.compact-question-bar {
    display: block;
    width: 16px;
    height: 3px;
    border-radius: 2px;
    background: var(--gray-400);
}

.compact-question-target:hover .compact-question-bar {
    background: var(--gray-500);
}

.compact-question-row.active .compact-question-bar,
.compact-question-row.active .compact-question-target:hover .compact-question-bar {
    background: var(--teal);
}
</style>
